/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { Bot, Maximize2, Minimize2, Minus, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';

import { isAgentV2EnabledForUser } from '@/lib/featureFlags';
import { selectUser } from '@/state/selectors/root-selectors';

import { EditorProvider } from '../demands-v2/editor/context/editor-provider';
import { AgentChatV2ThreadsPanel } from '../finch-chat/FinchChatThreadsPanel';
import { Z_INDEX_FINCH_ASSISTANT } from '../ui/zIndex';
import { AgentChatWithLeftSidebar } from './AgentChatUI';

export type ChatDrawerState = 'minimized' | 'drawer' | 'fullscreen';
type ChatVersion = 'agent_v2' | 'agent';

interface AgentChatDrawerProps {
  matterId: string;
  isOpen: boolean;
  onClose: () => void;
  initialChatVersion?: 'agent' | 'agent_v2' | null;
  initialThreadId?: string | null;
}

const ChatVersionToggle = ({
  chatVersion,
  onToggle,
}: {
  chatVersion: ChatVersion;
  onToggle: (v: ChatVersion) => void;
}) => (
  <div className="flex items-center gap-0.5 rounded-md bg-gray-200/60 p-0.5">
    <button
      type="button"
      onClick={() => onToggle('agent_v2')}
      className={`rounded px-2 py-0.5 text-[11px] font-medium transition-colors ${
        chatVersion === 'agent_v2'
          ? 'bg-white text-gray-900 shadow-sm'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      Agent V2
    </button>
    <button
      type="button"
      onClick={() => onToggle('agent')}
      className={`rounded px-2 py-0.5 text-[11px] font-medium transition-colors ${
        chatVersion === 'agent'
          ? 'bg-white text-gray-900 shadow-sm'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      Agent
    </button>
  </div>
);

export const AgentChatDrawer = ({
  matterId,
  isOpen,
  onClose,
  initialChatVersion,
  initialThreadId,
}: AgentChatDrawerProps) => {
  const user = useSelector(selectUser);
  const isAgentV2Enabled = isAgentV2EnabledForUser(user?.email);
  const [state, setState] = useState<ChatDrawerState>('drawer');
  const [isAnimating, setIsAnimating] = useState(false);
  const [chatVersion, setChatVersion] = useState<ChatVersion>(initialChatVersion === 'agent_v2' ? 'agent_v2' : 'agent');

  // Track whether the deeplink initial thread has been consumed at the drawer level
  // so it doesn't re-fire on drawer↔fullscreen transitions that remount child components.
  const deeplinkConsumedRef = useRef(false);
  const effectiveInitialThreadId = deeplinkConsumedRef.current ? undefined : initialThreadId;

  const handleDeeplinkConsumed = useCallback(() => {
    deeplinkConsumedRef.current = true;
  }, []);

  // Handle state transitions with animation
  const transitionToState = (newState: ChatDrawerState) => {
    setIsAnimating(true);
    setTimeout(() => {
      setState(newState);
      setIsAnimating(false);
    }, 150);
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && state === 'fullscreen') {
        transitionToState('drawer');
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      // Clean up resize listeners if component unmounts
    };
  }, [isOpen, state]);

  // When opening chat, use the initial chat version from deeplink if provided,
  // otherwise default to Agent (v1).
  useEffect(() => {
    if (isOpen) {
      if (initialChatVersion) {
        setChatVersion(initialChatVersion);
      } else {
        setChatVersion('agent');
      }
    }
  }, [isOpen, initialChatVersion]);

  if (!isOpen) return null;

  const renderContent = () => {
    // Minimized state - just a small button/bar
    if (state === 'minimized') {
      return (
        <div
          className={`fixed bottom-4 right-4 bg-white rounded-lg shadow-xl border border-gray-200 transition-all duration-300 ${
            isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
          }`}
          style={{ zIndex: Z_INDEX_FINCH_ASSISTANT }}
        >
          <div className="flex items-center p-3 gap-3 cursor-pointer hover:bg-gray-50 rounded-lg">
            <Bot className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-sm pr-2">Assistant Chat</span>
            <div className="flex items-center gap-1 ml-auto">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  transitionToState('drawer');
                }}
                className="p-1 hover:bg-gray-100 rounded"
                title="Open chat"
              >
                <Maximize2 className="h-4 w-4 text-gray-600" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="p-1 hover:bg-gray-100 rounded"
                title="Close"
              >
                <X className="h-4 w-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Drawer state - bottom right chat window
    if (state === 'drawer') {
      return (
        <div
          className="fixed bottom-4 right-4 bg-white rounded-lg shadow-2xl border border-gray-200 transition-all duration-300 flex flex-col w-156 h-156"
          style={{ zIndex: Z_INDEX_FINCH_ASSISTANT }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b bg-gray-50 rounded-t-lg flex-shrink-0">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-blue-600" />
              <span className="font-semibold text-sm">Assistant</span>
              {isAgentV2Enabled && (
                <ChatVersionToggle chatVersion={chatVersion} onToggle={setChatVersion} />
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => transitionToState('minimized')}
                className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                title="Minimize"
              >
                <Minus className="h-4 w-4 text-gray-600" />
              </button>
              <button
                type="button"
                onClick={() => transitionToState('fullscreen')}
                className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                title="Fullscreen"
              >
                <Maximize2 className="h-4 w-4 text-gray-600" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                title="Close"
              >
                <X className="h-4 w-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Chat content - now with proper flex and overflow */}
          <div className="flex-1 overflow-hidden rounded-b-lg min-h-0">
            {chatVersion === 'agent_v2' ? (
              <div className="h-full p-2">
                <AgentChatV2ThreadsPanel matterId={matterId} initialThreadId={initialChatVersion === 'agent_v2' ? effectiveInitialThreadId : undefined} onDeeplinkConsumed={handleDeeplinkConsumed} />
              </div>
            ) : (
              <EditorProvider>
                <AgentChatWithLeftSidebar
                  matterId={matterId}
                  viewMode="compact"
                  initialThreadId={initialChatVersion === 'agent' ? effectiveInitialThreadId : undefined}
                  onDeeplinkConsumed={handleDeeplinkConsumed}
                />
              </EditorProvider>
            )}
          </div>
        </div>
      );
    }

    // Fullscreen state - full modal
    return (
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ${
          isAnimating ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ zIndex: Z_INDEX_FINCH_ASSISTANT }}
        role="button"
        tabIndex={0}
        onClick={() => transitionToState('drawer')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            transitionToState('drawer');
          }
        }}
      >
        <div
          className={`fixed inset-4 bg-white rounded-lg shadow-2xl transition-all duration-300 flex flex-col ${
            isAnimating ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
          }`}
          style={{ zIndex: Z_INDEX_FINCH_ASSISTANT + 1 }}
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-lg flex-shrink-0">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-blue-600" />
              <span className="font-semibold">Assistant</span>
              {isAgentV2Enabled && (
                <ChatVersionToggle chatVersion={chatVersion} onToggle={setChatVersion} />
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => transitionToState('minimized')}
                className="p-2 hover:bg-gray-200 rounded transition-colors"
                title="Minimize"
              >
                <Minus className="h-4 w-4 text-gray-600" />
              </button>
              <button
                type="button"
                onClick={() => transitionToState('drawer')}
                className="p-2 hover:bg-gray-200 rounded transition-colors"
                title="Exit fullscreen"
              >
                <Minimize2 className="h-4 w-4 text-gray-600" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-gray-200 rounded transition-colors"
                title="Close"
              >
                <X className="h-4 w-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Chat content - fixed with flexbox */}
          <div className="flex-1 overflow-hidden rounded-b-lg min-h-0">
            {chatVersion === 'agent_v2' ? (
              <div className="h-full p-3">
                <AgentChatV2ThreadsPanel matterId={matterId} initialThreadId={initialChatVersion === 'agent_v2' ? effectiveInitialThreadId : undefined} onDeeplinkConsumed={handleDeeplinkConsumed} />
              </div>
            ) : (
              <EditorProvider>
                <AgentChatWithLeftSidebar
                  matterId={matterId}
                  viewMode="fullscreen"
                  initialThreadId={initialChatVersion === 'agent' ? effectiveInitialThreadId : undefined}
                  onDeeplinkConsumed={handleDeeplinkConsumed}
                />
              </EditorProvider>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Portal to render outside of current DOM hierarchy
  return createPortal(renderContent(), document.body);
};
